import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useSplioIntegrationUpdateMutation from "@babackapp/private-api-client/client/use-update-splio-integration";
import { Button } from "@babackapp/ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@babackapp/ui/components/form";
import { Input } from "@babackapp/ui/components/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import useTranslation from "@babackapp/translations/use-translation";
import { z } from "zod";
import IntegrationPreview from "./integration-preview";

const formSchema = z.object({
  activated: z.boolean(),
  token: z.string(),
});

function SplioIntegration() {
  const t = useTranslation();

  const { data: shop } = useCurrentShopSuspense();
  const { activated, token } = shop.integrations.splio;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { activated, token },
  });

  const { mutateAsync: updateSplioIntegration } =
    useSplioIntegrationUpdateMutation(shop);

  const onSubmit = form.handleSubmit((data) => {
    updateSplioIntegration(data).then(() => form.reset(data));
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col justify-start gap-4">
        <FormField
          control={form.control}
          name="token"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("settings.integrations.thirdpart.splio.splioToken")}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <Button type="submit">{t("misc.save")}</Button>
      </form>
    </Form>
  );
}

export function SplioIntegrationPreview() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const { mutateAsync: updateActivation } =
    useSplioIntegrationUpdateMutation(shop);

  const onActivationUpdate = useCallback(
    async (activated: boolean) => {
      await updateActivation({ activated });
    },
    [shop, updateActivation],
  );

  // @ts-expect-error Backend schema is wrong here ?
  if (!shop || !shop.plan.splio) return null;

  return (
    <IntegrationPreview
      title="Splio"
      bgColor="#00C39E"
      textColor="white"
      logo="/integrations/splio.png"
      description={t("settings.integrations.thirdpart.splio.description")}
      onActivationUpdate={onActivationUpdate}
      activated={shop.integrations.splio.activated}
      seeMoreUrl="https://splio.com"
    >
      <SplioIntegration />
    </IntegrationPreview>
  );
}
